import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import useTokenStore from "../store/store";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import { useUserProfile } from "../hooks/useUserProfile";
import { updateUserProfile, changePassword, ChangePasswordProps } from "../utils/user";
import { BarChart3, Zap, Settings, Home } from "lucide-react";
import AccountDeletionSection from "./components/AccountDeletionSection";
import ChangePasswordComponent from "./components/ChangePasswordComponent";

type ProfileInputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

function Profile() {
  const [message, setMessage] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isDeletionOpen, setIsDeletionOpen] = useState(false);
  const [searchParams] = useSearchParams();
  
  const navigate = useNavigate();
  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const { profile, loading, error, refetch } = useUserProfile();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfileInputs>();

  // Redirect if not logged in
  useEffect(() => {
    if (!getToken) {
      navigate("/login");
    }
  }, [getToken, navigate]);

  // Handle URL parameters to open specific sections and scroll to them
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (openParam === 'password') {
      setIsPasswordOpen(true);
      // Scroll to password section after a short delay to ensure it's rendered
      setTimeout(() => {
        const passwordSection = document.getElementById('change-password-section');
        if (passwordSection) {
          passwordSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    } else if (openParam === 'deletion') {
      setIsDeletionOpen(true);
      // Scroll to deletion section after a short delay to ensure it's rendered
      setTimeout(() => {
        const deletionSection = document.getElementById('account-deletion-section');
        if (deletionSection) {
          deletionSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    }
  }, [searchParams]);

  // Reset form when profile data changes
  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        phone: profile.phone || "",
      });
    }
  }, [profile, reset]);

  const onSubmit: SubmitHandler<ProfileInputs> = async (data: ProfileInputs) => {
    setMessage("");
    
    if (!getToken) {
      setMessage("Authentication token not found. Please log in again.");
      return;
    }
    
    try {
      // Make API call to update the user profile
      const result = await updateUserProfile(getToken, data);
      
      if (result.status === 200) {
        setMessage("Profile updated successfully!");
        setIsEditing(false);
        // Refetch profile data to get the updated information
        refetch();
      } else {
        setMessage(`Failed to update profile: ${result.message}`);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage("Failed to update profile. Please try again.");
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (profile) {
      reset({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        phone: profile.phone || "",
      });
    }
  };

  // Add password change handler
  const handlePasswordChange = async (passwordData: ChangePasswordProps) => {
    setPasswordMessage("");
    
    if (!getToken) {
      setPasswordMessage("Authentication token not found. Please log in again.");
      throw new Error("No token");
    }
    
    setIsPasswordLoading(true);
    
    try {
      const result = await changePassword(getToken, passwordData);
      
      if (result.status === 200) {
        setPasswordMessage("Password updated successfully!");
        // Clear message after 5 seconds
        setTimeout(() => setPasswordMessage(""), 5000);
      } else {
        setPasswordMessage(`Failed to update password: ${result.message}`);
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Error updating password:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to update password. Please try again.";
      setPasswordMessage(errorMessage);
      throw error; // Re-throw to handle in component
    } finally {
      setIsPasswordLoading(false);
    }
  };

  // Clear password message after 10 seconds
  useEffect(() => {
    if (passwordMessage) {
      const timer = setTimeout(() => setPasswordMessage(""), 10000);
      return () => clearTimeout(timer);
    }
  }, [passwordMessage]);

  // White color detection function
  const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
    
    if (s === "white" || s === "#ffffff" || s === "#fff" || s === "rgb(255, 255, 255)" || s === "rgba(255, 255, 255, 1)") {
      return true;
    }
    
    if (s.startsWith("#") && s.length === 7) {
      const hex = s.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 240;
    }
    
    if (s.startsWith("rgb")) {
      const matches = s.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]);
        const g = parseInt(matches[1]);
        const b = parseInt(matches[2]);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 240;
      }
    }
    
    return false;
  };

  // Get contrasting text color for better visibility
  const getContrastColor = (backgroundColor: string): string => {
    return isWhiteOrShade(backgroundColor) ? "#1f2937" : "#ffffff";
  };

  if (!getToken) {
    return null; // Will redirect to login
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: getThemes?.color1 }}></div>
          <p style={{ color: getThemes?.txtColor }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading profile: {error}</p>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p style={{ color: getThemes?.txtColor }}>No profile data available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>My Profile | {getThemes?.name || 'Property Investment Platform'}</title>
        <meta name="description" content="Manage your profile and account settings" />
      </Helmet>

      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-2" style={{ color: getThemes?.txtColor }}>
              My Profile
            </h1>
            <p className="text-lg" style={{ color: getThemes?.txtColor }}>
              Manage your account information and preferences
            </p>
          </div>

          {/* Profile Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 ease-in-out">
            {/* Profile Header */}
            <div
              className="px-8 py-6 text-center"
              style={{ background: getThemes?.color1 }}
            >
              <div className="flex justify-center mb-4">
                <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center overflow-hidden border-4 border-white border-opacity-30">
                  <span 
                    className="text-3xl font-bold"
                    style={{ color: getContrastColor(getThemes?.color1 || "") }}
                  >
                    {profile.firstName?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </div>
              <h2 
                className="text-2xl font-bold mb-2" 
                style={{ color: getContrastColor(getThemes?.color1 || "") }}
              >
                {profile.firstName} {profile.lastName}
              </h2>
              <p 
                className="text-lg opacity-90" 
                style={{ color: getContrastColor(getThemes?.color1 || "") }}
              >
                {profile.userHandle}
              </p>
            </div>

            {/* Profile Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Message */}
                {message && (
                  <div
                    className={`p-4 rounded-lg ${
                      message.includes("success") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}
                  >
                    {message}
                  </div>
                )}

                {/* First Name & Last Name Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      First Name
                    </label>
                    {isEditing ? (
                      <input
                        {...register("firstName", { required: "First name is required" })}
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 border border-gray-200">
                        {profile.firstName || "Not provided"}
                      </div>
                    )}
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Last Name
                    </label>
                    {isEditing ? (
                      <input
                        {...register("lastName", { required: "Last name is required" })}
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 border border-gray-200">
                        {profile.lastName || "Not provided"}
                      </div>
                    )}
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                {/* Email & Phone Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Email Address
                    </label>
                    {isEditing ? (
                      <input
                        {...register("email", { 
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                        type="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 border border-gray-200">
                        {profile.email || "Not provided"}
                      </div>
                    )}
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Phone Number
                    </label>
                    {isEditing ? (
                      <input
                        {...register("phone", { required: "Phone is required" })}
                        type="tel"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 border border-gray-200">
                        {profile.phone || "Not provided"}
                      </div>
                    )}
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-100">
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={handleEdit}
                      className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                      style={{
                        backgroundColor: getThemes?.color1 || "#0055cc",
                        color: getContrastColor(getThemes?.color1 || "#0055cc"),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = getThemes?.color2 || "#0066dd";
                        e.currentTarget.style.color = getContrastColor(getThemes?.color2 || "#0066dd");
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = getThemes?.color1 || "#0055cc";
                        e.currentTarget.style.color = getContrastColor(getThemes?.color1 || "#0055cc");
                      }}
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                        style={{
                          backgroundColor: getThemes?.color1 || "#0055cc",
                          color: getContrastColor(getThemes?.color1 || "#0055cc"),
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = getThemes?.color2 || "#0066dd";
                          e.currentTarget.style.color = getContrastColor(getThemes?.color2 || "#0066dd");
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = getThemes?.color1 || "#0055cc";
                          e.currentTarget.style.color = getContrastColor(getThemes?.color1 || "#0055cc");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                        style={{
                          backgroundColor: getThemes?.color1 || "#0055cc",
                          color: getContrastColor(getThemes?.color1 || "#0055cc"),
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = getThemes?.color2 || "#0066dd";
                          e.currentTarget.style.color = getContrastColor(getThemes?.color2 || "#0066dd");
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = getThemes?.color1 || "#0055cc";
                          e.currentTarget.style.color = getContrastColor(getThemes?.color1 || "#0055cc");
                        }}
                      >
                        Save Changes
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>

          
          {/* Change Password Section */}
            <div id="change-password-section">
              <ChangePasswordComponent
                onUpdatePassword={handlePasswordChange}
                isLoading={isPasswordLoading}
                themeColor={getThemes?.color1 || "#3B82F6"}
                themeColor2={getThemes?.color2}
                isOpen={isPasswordOpen}
                onToggleOpen={setIsPasswordOpen}
              />
            </div>

          {/* Password Message */}
          {passwordMessage && (
            <div className={`mt-4 p-3 rounded-md ${
              passwordMessage.includes("successfully") 
                ? "bg-green-50 text-green-800 border border-green-200" 
                : "bg-red-50 text-red-800 border border-red-200"
            }`}>
              {passwordMessage}
            </div>
          )}

           {/* Account Deletion Section */}
           <div id="account-deletion-section">
             <AccountDeletionSection 
               profile={{
                 email: profile.email,
                 deletionRequested: profile.deletionRequested,
                 deletionScheduledFor: profile.deletionScheduledFor,
                 deletionCancellationToken: profile.deletionCancellationToken,
                 accountStatus: profile.accountStatus
               }}
               onDeletionUpdate={refetch}
               isOpen={isDeletionOpen}
               onToggleOpen={setIsDeletionOpen}
             />
           </div>


          {/* Bottom Cards Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Account Information */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 ease-in-out cursor-pointer">
              <div className="flex items-center mb-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                  style={{ backgroundColor: getThemes?.color1 }}
                >
                  <BarChart3 className="w-5 h-5" style={{ color: getThemes?.txtColor }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Account Information
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">Member Since</span>
                  <span className="text-gray-900 font-semibold">
                    {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Recently'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">Last Updated</span>
                  <span className="text-gray-900 font-semibold">
                    {profile.updatedAt ? new Date(profile.updatedAt).toLocaleDateString() : 'Recently'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">Account Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${profile.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {profile.status ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">Verification</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${profile.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {profile.verified ? 'Verified' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 ease-in-out cursor-pointer">
              <div className="flex items-center mb-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                  style={{ backgroundColor: getThemes?.color1 }}
                >
                  <Zap className="w-5 h-5" style={{ color: getThemes?.txtColor }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Quick Actions
                </h3>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
                >
                  <BarChart3 className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200 text-black" />
                  <div>
                    <div className="font-medium text-gray-900">View Dashboard</div>
                    <div className="text-sm text-gray-500">See your overview and stats</div>
                  </div>
                </button>
                <button
                  onClick={() => navigate("/settings")}
                  className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
                >
                  <Settings className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200 text-black" />
                  <div>
                    <div className="font-medium text-gray-900">Account Settings</div>
                    <div className="text-sm text-gray-500">Manage preferences and security</div>
                  </div>
                </button>
                <button
                  onClick={() => navigate("/listing")}
                  className="w-full flex items-center px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
                >
                  <Home className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200 text-black" />
                  <div>
                    <div className="font-medium text-gray-900">Browse Properties</div>
                    <div className="text-sm text-gray-500">Explore investment opportunities</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Profile;